/*
  NAAMANE productslider: tabs, pijlen, voortgangsbalk, slepen met de muis en snel toevoegen.
  Snel toevoegen volgt dezelfde route als het productformulier van het theme,
  zodat de winkelwagen-lade opent en het aantal in de header klopt.
*/
import { CartLinesUpdateEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

class NmRail extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('.nm-rail__panel')];
    this.arrows = [...this.querySelectorAll('[data-dir]')];
    this.bar = this.querySelector('.nm-rail__progress span');
    this.status = this.querySelector('.nm-rail__status');

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      });
    });
    this.arrows.forEach((a) => a.addEventListener('click', () => this.page(Number(a.dataset.dir))));

    this._onScroll = () => this.update();
    this.panels.forEach((p) => {
      const track = p.querySelector('.nm-rail__track');
      if (!track) return;
      track.addEventListener('scroll', this._onScroll, { passive: true });
      this.enableDrag(track);
    });
    window.addEventListener('resize', this._onScroll, { passive: true });

    this.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-quick-toggle]');
      if (toggle) {
        const card = toggle.closest('.nm-card');
        const open = !card.classList.contains('is-open');
        this.querySelectorAll('.nm-card.is-open').forEach((c) => {
          c.classList.remove('is-open');
          c.querySelector('[data-quick-toggle]')?.setAttribute('aria-expanded', 'false');
        });
        card.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', String(open));
        return;
      }
      const add = e.target.closest('[data-add]');
      if (add && !add.disabled) this.add(add);
    });

    this.update();
  }

  disconnectedCallback() {
    this._ready = false;
    window.removeEventListener('resize', this._onScroll);
  }

  get track() {
    const panel = this.panels.find((p) => !p.hidden);
    return panel ? panel.querySelector('.nm-rail__track') : null;
  }

  select(index) {
    this.tabs.forEach((t, i) => {
      const on = i === index;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    });
    this.panels.forEach((p, i) => {
      p.hidden = i !== index;
    });
    if (this.track) this.track.scrollLeft = 0;
    this.update();
  }

  page(dir) {
    const t = this.track;
    if (!t) return;
    t.scrollBy({ left: dir * t.clientWidth * 0.85, behavior: 'smooth' });
  }

  update() {
    const t = this.track;
    if (!t) return;
    const max = t.scrollWidth - t.clientWidth;
    const ratio = t.scrollWidth ? Math.min(1, t.clientWidth / t.scrollWidth) : 1;
    const pos = max > 0 ? t.scrollLeft / max : 0;
    if (this.bar) {
      this.bar.style.width = `${ratio * 100}%`;
      this.bar.style.transform = `translateX(${pos * (1 / ratio - 1) * 100}%)`;
    }
    const [prev, next] = this.arrows;
    if (prev) prev.disabled = t.scrollLeft <= 2;
    if (next) next.disabled = t.scrollLeft >= max - 2;
  }

  enableDrag(track) {
    let startX = 0;
    let startLeft = 0;
    let moved = false;
    let down = false;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('button')) return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = track.scrollLeft;
    });
    track.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 6) {
        moved = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(e.pointerId);
      }
      if (moved) track.scrollLeft = startLeft - dx;
    });
    const end = (e) => {
      if (!down) return;
      down = false;
      if (moved) {
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture(e.pointerId)) track.releasePointerCapture(e.pointerId);
        const card = track.querySelector('.nm-rail__item');
        const step = card ? card.getBoundingClientRect().width + 6 : track.clientWidth;
        track.scrollTo({ left: Math.round(track.scrollLeft / step) * step, behavior: 'smooth' });
      }
    };
    track.addEventListener('dragstart', (e) => e.preventDefault());
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('click', (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);
  }

  async add(button) {
    const id = button.dataset.add;
    button.classList.add('is-busy');
    const label = button.textContent;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await res.json();
      const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const failed = !res.ok || Boolean(data.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'naamane-rail',
          sourceId: id,
          itemCount: 1,
          sections: failed ? undefined : data.sections,
          didError: failed,
        },
      });
      this.say(failed ? data.description || 'Toevoegen is niet gelukt.' : 'Toegevoegd aan je winkelwagen.');
      if (!failed) {
        try {
          window.Shopify?.analytics?.publish?.('naamane_quick_add', {
            variantId: String(id),
            source: this.classList.contains('nm-upsell') ? 'cart_upsell' : this.classList.contains('nm-look') ? 'complete_the_look' : 'product_rail',
          });
        } catch (e) { /* niet kritisch */ }
        button.classList.add('is-done');
        button.textContent = '✓';
        setTimeout(() => {
          button.classList.remove('is-done');
          button.textContent = label;
          button.closest('.nm-card')?.classList.remove('is-open');
        }, 1400);
      }
    } catch (error) {
      deferred.reject(error);
      this.say('Toevoegen is niet gelukt. Probeer het opnieuw.');
    } finally {
      button.classList.remove('is-busy');
    }
  }

  say(text) {
    if (!this.status) return;
    this.status.textContent = '';
    requestAnimationFrame(() => {
      this.status.textContent = text;
    });
  }
}

if (!customElements.get('nm-rail')) customElements.define('nm-rail', NmRail);
